import React from 'react';
import styled, { useTheme } from 'styled-components';
import { FaExchangeAlt, FaSpinner, FaChartLine } from 'react-icons/fa';
import { BiCake } from 'react-icons/bi';

const SelectContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const ExchangeOptionsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const ExchangeOption = styled.div`
  flex: 1;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 2px solid ${props => props.selected ? props.theme.colors.accent : props.theme.colors.border};
  background-color: ${props => props.selected ? props.theme.colors.primaryAlpha(0.1) : props.theme.colors.background.card};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  position: relative;
  
  &:hover {
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    font-size: 2.5rem;
    color: ${props => props.selected ? props.color : props.theme.colors.text.muted};
    transition: color ${props => props.theme.transitions.fast};
  }
  
  .exchange-name {
    font-weight: 600;
    color: ${props => props.selected ? props.theme.colors.text.primary : props.theme.colors.text.muted};
    font-size: ${props => props.theme.fontSize.sm};
  }
  
  .exchange-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background: linear-gradient(45deg, #ff6b6b, #feca57);
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: bold;
    text-transform: uppercase;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.md};
  
  svg {
    animation: spin 1s linear infinite;
    color: ${props => props.theme.colors.accent};
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ExchangeSelector = ({ selectedExchange = '', onExchangeChange, loading = false }) => {
  const theme = useTheme();
  
  // Define exchange options
  const exchangeOptions = [
    { id: 'pancakeswap', name: 'PancakeSwap', icon: BiCake, color: theme.colors.warning },
    { id: 'hyperliquid', name: 'Hyperliquid', icon: FaChartLine, color: theme.colors.success }
  ];
  
  const handleExchangeSelect = (exchangeId) => {
    if (onExchangeChange) {
      onExchangeChange(exchangeId);
    }
  };
  
  if (loading) {
    return (
      <SelectContainer>
        <Label htmlFor="exchange">
          <FaExchangeAlt />
          <span>Loading Exchanges...</span>
        </Label>
        <LoadingSpinner>
          <FaSpinner size={24} />
        </LoadingSpinner>
      </SelectContainer>
    );
  }
  
  return (
    <SelectContainer>
      <Label htmlFor="exchange">
        <FaExchangeAlt />
        <span>Select Exchange</span>
      </Label>
      
      <ExchangeOptionsContainer>
        {exchangeOptions.map(exchange => {
          const Icon = exchange.icon;
          const isSelected = selectedExchange === exchange.id;
          
          return (
            <ExchangeOption 
              key={exchange.id} 
              selected={isSelected}
              color={exchange.color}
              onClick={() => handleExchangeSelect(exchange.id)}
            >
              {exchange.badge && <div className="exchange-badge">{exchange.badge}</div>}
              <Icon />
              <span className="exchange-name">{exchange.name}</span>
            </ExchangeOption>
          );
        })}
      </ExchangeOptionsContainer>
    </SelectContainer>
  );
};

export default ExchangeSelector; 